import { Recipe } from '../types/recipe';

export const STORAGE_KEYS = {
  FILLINGS: 'fillings-recipes',
  COMPONENTS: 'components-recipes',
  OLD_RECIPES: 'recipe-templates' // Legacy key for migration
} as const;

/**
 * Loads recipes from localStorage with migration support
 */
export function loadRecipesFromStorage(key: string): Recipe[] {
  console.log(`[Migration] Loading recipes from: ${key}`);

  try {
    // Migration: move recipes from old key to fillings if needed
    if (key === STORAGE_KEYS.FILLINGS) {
      const oldStored = localStorage.getItem(STORAGE_KEYS.OLD_RECIPES);
      const currentStored = localStorage.getItem(STORAGE_KEYS.FILLINGS);

      if (oldStored && !currentStored) {
        localStorage.setItem(STORAGE_KEYS.FILLINGS, oldStored);
        console.log('[Migration] ✓ Migrated recipes from recipe-templates to fillings-recipes');
      } else if (oldStored) {
        console.log('[Migration] Old recipes exist but fillings already has data - skipping migration');
      } else {
        console.log('[Migration] No old recipes found to migrate');
      }
    }

    const stored = localStorage.getItem(key);
    if (!stored) {
      console.log(`[Migration] No data found in ${key}`);
      return [];
    }

    const parsed = JSON.parse(stored);

    // Validate parsed data
    if (!Array.isArray(parsed)) {
      console.warn(`[Migration] Data in ${key} is not an array, got:`, typeof parsed);
      return [];
    }

    console.log(`[Migration] Found ${parsed.length} recipes in ${key}`);

    // Migration: add yield field and amountDisplay to existing recipes
    const migrated = parsed.map((recipe: any, index: number) => {
      let modified = false;

      // Ensure recipe has an ID
      if (!recipe.id) {
        console.warn(`[Migration] Recipe ${index} missing ID, generating new one`);
        recipe.id = crypto.randomUUID();
        modified = true;
      }

      // Ensure recipe has a name
      if (!recipe.name) {
        console.warn(`[Migration] Recipe ${index} missing name, using default`);
        recipe.name = 'Unnamed Recipe';
        modified = true;
      }

      // Add yield field if missing
      if (!recipe.yield) {
        recipe.yield = 1;
        modified = true;
      }

      // Ensure ingredients array exists
      if (!recipe.ingredients) {
        recipe.ingredients = [];
        modified = true;
      }

      // Migrate ingredients
      if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
        recipe.ingredients = recipe.ingredients.map((ing: any, ingIndex: number) => {
          // Ensure ingredient has an ID
          if (!ing.id) {
            ing.id = crypto.randomUUID();
          }

          // Add amountDisplay if missing
          if (!ing.amountDisplay) {
            ing.amountDisplay = String(ing.amount || '');
          }

          // Add type if missing
          if (!ing.type) {
            ing.type = ing.componentId ? 'component' : 'ingredient';
          }

          // Ensure name exists
          if (!ing.name) {
            ing.name = '';
          }

          // Ensure unit exists
          if (!ing.unit) {
            ing.unit = '';
          }

          // Ensure amount is a number
          if (typeof ing.amount !== 'number') {
            ing.amount = parseFloat(String(ing.amount)) || 0;
          }

          return ing;
        });
      }

      if (modified) {
        console.log(`[Migration] Updated recipe: ${recipe.name}`);
      }

      return recipe;
    });

    console.log(`[Migration] ✓ Successfully loaded and migrated ${migrated.length} recipes from ${key}`);
    return migrated;
  } catch (error) {
    console.error(`[Migration] ✗ Failed to load recipes from storage (${key}):`, error);
    return [];
  }
}

/**
 * Saves recipes to localStorage
 */
export function saveRecipesToStorage(key: string, recipes: Recipe[]): void {
  try {
    const json = JSON.stringify(recipes);
    localStorage.setItem(key, json);
  } catch (error) {
    console.error(`Failed to save recipes to storage (${key}):`, error);
  }
}
